import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { getTooltipStyle } from '../../../hooks/useExpandedStyles';
import { Zap, Factory, Building2, Wifi, Sprout, Home } from 'lucide-react';

const getSectorColor = (sector: string): string => {
  const colors: Record<string, string> = {
    'Energy & Mining': '#ffb800',
    'Manufacturing': '#00a3ff',
    'Financial Services': '#00d26a',
    'Telecoms & IT': '#06b6d4',
    'Infrastructure': '#a855f7',
    'Agriculture': '#84cc16',
    'Real Estate': '#f97316',
    'Other': '#6b7280',
  };
  return colors[sector] || '#6b7280';
};

const getSectorIcon = (sector: string): React.ReactNode => {
  const icons: Record<string, React.ReactNode> = {
    'Energy & Mining': <Zap size={16} />,
    'Manufacturing': <Factory size={16} />,
    'Financial Services': <Building2 size={16} />,
    'Telecoms & IT': <Wifi size={16} />,
    'Agriculture': <Sprout size={16} />,
    'Real Estate': <Home size={16} />,
  };
  return icons[sector] || null;
};

interface SectorBreakdownPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

export const SectorBreakdownPanel: React.FC<SectorBreakdownPanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { sectors } = fdiDetailData;

  const maxShare = Math.max(...sectors.map((s) => s.share));
  const fastestGrowing = sectors.reduce((prev, curr) => (curr.yoy > prev.yoy ? curr : prev));
  const totalValue = sectors.reduce((sum, s) => sum + s.value, 0);

  // Prepare pie chart data
  const pieData = sectors.slice(0, 6).map((s) => ({
    name: s.sector,
    value: s.share,
  }));

  const expandedContent = (
    <div className="flex flex-col h-full gap-4 md:gap-8">
      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Total Sectors</div>
          <div className="text-xl md:text-3xl font-bold text-white">{sectors.length}</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Active in 2024</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Total FDI</div>
          <div className="text-xl md:text-3xl font-bold text-data-green">${totalValue.toFixed(1)}B</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">Across all sectors</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Top Sector</div>
          <div className="text-xl md:text-3xl font-bold text-data-amber">{sectors[0].share}%</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">{sectors[0].sector}</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-3 md:p-5">
          <div className="text-xs md:text-sm text-gray-500 uppercase mb-1 md:mb-2">Fastest Growing</div>
          <div className="text-xl md:text-3xl font-bold text-data-green">+{fastestGrowing.yoy}%</div>
          <div className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2">{fastestGrowing.sector}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Pie Chart */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            Sector Distribution
          </h3>
          <div className="h-[200px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="30%"
                  outerRadius="55%"
                  paddingAngle={2}
                  label={({ name, percent }) => `${(name as string)?.split(' ')[0] || ''} ${((percent as number) * 100).toFixed(0)}%`}
                  labelLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={getSectorColor(entry.name)} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number, name: string) => [`${value}%`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart - Investment by Sector */}
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
            Investment Volume (USD Billions)
          </h3>
          <div className="h-[200px] md:h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sectors} layout="vertical" margin={{ top: 10, right: 20, bottom: 10, left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => `$${v}B`} />
                <YAxis type="category" dataKey="sector" tick={{ fontSize: 9, fill: '#9ca3af' }} width={75} />
                <Tooltip contentStyle={getTooltipStyle(true)} formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Investment']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {sectors.map((entry, index) => (
                    <Cell key={index} fill={getSectorColor(entry.sector)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Sector Cards */}
      <div>
        <h3 className="text-sm md:text-base font-semibold text-gray-200 uppercase tracking-wide mb-3 md:mb-4">
          Sector Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
          {sectors.slice(0, 6).map((s) => (
            <div
              key={s.sector}
              className="bg-terminal-bg rounded-lg p-3 md:p-5 border-l-4"
              style={{ borderColor: getSectorColor(s.sector) }}
            >
              <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
                <div className="text-gray-400">{getSectorIcon(s.sector)}</div>
                <h4 className="text-white font-semibold text-sm md:text-base">{s.sector}</h4>
              </div>
              <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
                <div>
                  <div className="text-xs md:text-sm text-gray-500 uppercase">Value</div>
                  <div className="text-base md:text-lg font-bold text-white">${s.value.toFixed(2)}B</div>
                </div>
                <div>
                  <div className="text-xs md:text-sm text-gray-500 uppercase">Share</div>
                  <div className="text-base md:text-lg font-bold" style={{ color: getSectorColor(s.sector) }}>
                    {s.share}%
                  </div>
                </div>
                <div>
                  <div className="text-xs md:text-sm text-gray-500 uppercase">YoY</div>
                  <div className={`text-base md:text-lg font-bold ${s.yoy > 50 ? 'text-data-amber' : 'text-data-green'}`}>
                    +{s.yoy}%
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-3 md:mt-4 h-1.5 md:h-2 bg-terminal-border rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${(s.share / maxShare) * 100}%`,
                    backgroundColor: getSectorColor(s.sector),
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sector Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h4 className="text-sm md:text-base font-semibold text-gray-300 mb-3 md:mb-4">Growth Leaders</h4>
          <ul className="space-y-2 md:space-y-3">
            {sectors
              .sort((a, b) => b.yoy - a.yoy)
              .slice(0, 4)
              .map((s, i) => (
                <li key={s.sector} className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">
                    #{i + 1} {s.sector}
                  </span>
                  <span className="text-data-green font-semibold">+{s.yoy}% YoY</span>
                </li>
              ))}
          </ul>
        </div>
        <div className="bg-terminal-bg rounded-lg p-4 md:p-6">
          <h4 className="text-sm md:text-base font-semibold text-gray-300 mb-3 md:mb-4">Key Developments</h4>
          <ul className="space-y-2 md:space-y-3 text-xs md:text-sm text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-data-amber">•</span>
              Energy sector driven by renewable projects
            </li>
            <li className="flex items-start gap-2">
              <span className="text-data-blue">•</span>
              Manufacturing expanding with auto assembly
            </li>
            <li className="flex items-start gap-2">
              <span className="text-data-green">•</span>
              Fintech attracting significant interest
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              Agriculture modernization ongoing
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const compactContent = (
    <div className="flex flex-col h-full p-3">
      {/* Hero - Top Sector */}
      <div className="text-center py-4 px-4 bg-gradient-to-b from-data-amber/10 to-transparent rounded-lg border border-data-amber/20 mb-4">
        <div className="flex items-center justify-center gap-2 mb-2">
          {getSectorIcon(sectors[0].sector)}
          <span className="text-xs text-data-amber uppercase tracking-widest font-semibold">
            Top Sector
          </span>
        </div>
        <div className="text-lg font-semibold text-white mb-1">{sectors[0].sector}</div>
        <div className="text-3xl font-mono font-black text-data-amber">{sectors[0].share}%</div>
        <div className="text-xs text-gray-500 mt-1">${sectors[0].value.toFixed(2)}B invested</div>
      </div>

      {/* Sector Bars */}
      <div className="flex-1 space-y-3">
        {sectors.slice(0, 5).map((s, idx) => (
          <div key={s.sector}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="text-gray-400">{getSectorIcon(s.sector)}</span>
                <span className="text-xs text-gray-300">{s.sector}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono font-bold text-white">{s.share}%</span>
                <span className={`text-xs ${s.yoy > 50 ? 'text-data-amber' : 'text-data-green'}`}>
                  +{s.yoy}%
                </span>
              </div>
            </div>
            <div className="h-2.5 bg-terminal-border rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${(s.share / maxShare) * 100}%`,
                  backgroundColor: getSectorColor(s.sector),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-auto pt-3 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="SECTOR BREAKDOWN"
      subtitle="FDI BY INDUSTRY"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default SectorBreakdownPanel;
